import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { translations } from '@aws-amplify/ui-react';
import { I18n } from 'aws-amplify/utils';
import { WeddingProvider, useWeddingContext } from './context/WeddingContext';
import CustomAuthenticator from './components/CustomAuthenticator';
import Header from './components/Header';
import TabNavigation from './components/TabNavigation';
import Dashboard from './components/Dashboard';
import TaskList from './components/TaskList';
import AddTask from './components/AddTask';
import Settings from './components/Settings';
import InvitePage from './components/InvitePage';
import awsConfig from './config/aws-config';
import '@aws-amplify/ui-react/styles.css';
import './App.css';

// Amplify設定
const amplifyConfig = {
  aws_project_region: awsConfig.cognito.region,
  aws_user_pools_id: awsConfig.cognito.userPoolId,
  aws_user_pools_web_client_id: awsConfig.cognito.userPoolWebClientId
};

console.log('Amplify Config:', amplifyConfig);
console.log('AWS Config:', awsConfig);

// 日本語翻訳設定
I18n.putVocabularies({
  ja: {
    ...translations.ja,
    'Sign In': 'ログイン',
    'Sign Up': '新規登録',
    'Username': 'メールアドレス（ユーザー名）',
    'Enter your Username': 'メールアドレス（ユーザー名）を入力してください',
    'Enter your Email': 'メールアドレスを入力してください',
    'Enter your Password': 'パスワードを入力してください',
    'Forgot your password?': 'パスワードをお忘れですか？',
    'Reset password': 'パスワードリセット',
    'Back to Sign In': 'ログイン画面に戻る',
    'Send Code': 'コードを送信',
    'Code': '確認コード',
    'New Password': '新しいパスワード',
    'Confirm Password': 'パスワード確認',
    'Submit': '送信',
    'Resend Code': 'コードを再送信',
    'Password': 'パスワード',
    'Email': 'メールアドレス',
    'We Emailed You': 'メールを送信しました',
    'Your code is on the way. To log in, enter the code we emailed to': 'ご登録のメールアドレスに確認コードを送信しました。以下のアドレスに送信されたコードを入力してください:',
    'It may take a minute to arrive.': '受信まで1分程度お時間をいただく場合があります。',
    'Your passwords must match': 'パスワードが一致しません',
    'Password must have at least 8 characters': 'パスワードは8文字以上で入力してください',
    'Password must have lower case letters': 'パスワードに小文字を含めてください',
    'Password must have upper case letters': 'パスワードに大文字を含めてください',
    'Password must have numbers': 'パスワードに数字を含めてください',
    'Password must have special characters': 'パスワードに特殊文字を含めてください',
    'Incorrect username or password.': 'メールアドレスまたはパスワードが正しくありません。',
    'User does not exist.': 'このメールアドレスは登録されていません。',
    'User already exists': 'このメールアドレスは既に登録されています。',
    'Invalid verification code provided, please try again.': '確認コードが正しくありません。再度お試しください。',
    'Invalid password format': 'パスワードの形式が正しくありません',
    'Account confirmation necessary. Check your email.': 'アカウントの確認が必要です。メールをご確認ください。',
    'Network error': 'ネットワークエラーが発生しました',
    'Loading...': '読み込み中...',
    'Sign in': 'ログイン',
    'Creating Account': 'アカウント作成中',
    'Confirm Sign Up': 'サインアップ確認',
    'Create Account': 'アカウント作成'
  }
});

I18n.setLanguage('ja');

// @ts-ignore
Amplify.configure(amplifyConfig);

// 認証済みアプリケーション内でのウェディンググループ作成処理
const AuthenticatedAppWithGroupCreation: React.FC<{ 
  pendingGroupData?: { familyId: string; familyName?: string };
  user: any;
  signOut: () => void;
}> = ({ 
  pendingGroupData,
  user: authUser,
  signOut
}) => {
    const { state, hasWeddingGroup, isLoading, isInitialized, weddingGroup, user, isAuthenticated, createSimpleWeddingGroup, setCurrentView } = useWeddingContext();
  const location = useLocation();
  const [showLoading, setShowLoading] = useState(false);

  // ローディング表示の遅延処理（ちらつき防止）
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setShowLoading(true);
      }, 150); // 150ms後に表示開始
      
      return () => clearTimeout(timer);
    } else {
      setShowLoading(false);
    }
  }, [isLoading]);

  // ペンディングのウェディンググループ作成を処理
  useEffect(() => {
    console.log('🔄 AuthenticatedApp: useEffect triggered for family creation');
    console.log('📊 AuthenticatedApp: Current state:', {
      pendingGroupData: !!pendingGroupData,
      pendingGroupDataDetails: pendingGroupData,
      user: !!user,
      hasWeddingGroup,
      isLoading,
      isInitialized
    });
    
    // 初期化完了後のみ処理
      if (pendingGroupData && user && !hasWeddingGroup && !isLoading && isInitialized) {
      console.log('🎯 AuthenticatedApp: All conditions met - starting family creation');
      console.log('📝 AuthenticatedApp: Creating wedding group with data:', pendingGroupData);
      
      createSimpleWeddingGroup(pendingGroupData.familyId, pendingGroupData.familyName)
        .then(() => {
          console.log('✅ AuthenticatedApp: Wedding group created successfully');
        })
          .catch((error: any) => {
          console.error('❌ AuthenticatedApp: Failed to create wedding group:', error);
          console.error('❌ AuthenticatedApp: Error details:', {
            message: error.message,
            stack: error.stack,
            type: error.constructor.name
          });
        });
    } else {
      console.log('⏸️ AuthenticatedApp: Family creation conditions not met:', {
        pendingGroupData: !!pendingGroupData,
        user: !!user,
        hasWeddingGroup,
        isLoading,
        isInitialized,
        reason: !pendingGroupData ? 'No pending data' :
                !user ? 'No user' :
                hasWeddingGroup ? 'Already has wedding group' :
                isLoading ? 'Still loading' :
                !isInitialized ? 'Not initialized' : 'Unknown'
      });
    }
    }, [pendingGroupData, user, hasWeddingGroup, isLoading, isInitialized, createSimpleWeddingGroup]);

  // デバッグ情報をコンソールに出力（簡素化）
  console.log('AuthenticatedApp Debug:', {
    hasWeddingGroup,
    isLoading,
      isInitialized,
      isFullyInitialized: user && isAuthenticated && isInitialized,
    weddingGroup: !!weddingGroup,
    user: !!user,
    isAuthenticated
  });

  // **認証とデータの初期化状態を厳密に確認**
  const isFullyInitialized = user && isAuthenticated && isInitialized;

  // 招待ページは独立して処理
  if (location.pathname.startsWith('/invite')) {
    return <InvitePage />;
  }

  // **ローディング状態**: 初期化中またはデータ読み込み中
  if (showLoading || !isFullyInitialized) {
    return (
      <div className="min-h-screen bg-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600 mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">読み込み中...</p>
        </div>
      </div>
    );
  }

      // **データ取得完全完了後のみ**: ウェディンググループ未設定の場合はエラー画面を表示
    if (isFullyInitialized && !hasWeddingGroup) {
    return (
      <div className="min-h-screen bg-red-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center border border-red-200">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-red-800 mb-4">アカウントエラー</h2>
            <p className="text-red-600 mb-6">
              有効なファミリーアカウントが見つかりません。<br />
              アカウントが正しく設定されていない可能性があります。
            </p>
            
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-800 mb-2">🔧 解決方法</h3>
                <ul className="text-sm text-red-700 text-left space-y-1">
                  <li>• 新しいアカウントを作成してください</li>
                  <li>• 既存のファミリーアカウントからの招待を受けてください</li>
                  <li>• 問題が続く場合はサポートにお問い合わせください</li>
                </ul>
              </div>
              
              <button 
                onClick={() => {
                  if (window.confirm('ログアウトして新しいアカウントを作成しますか？')) {
                    signOut();
                  }
                }} 
                className="w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                ログアウト
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderCurrentView = () => {
    switch (state.currentView) {
      case 'dashboard':
        return <Dashboard />;
      case 'tasks':
        return <TaskList />;
      case 'add-task':
        return <AddTask />;
      case 'settings':
        return <Settings user={authUser} signOut={signOut} />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-pink-50">
      <div className="container mx-auto p-4 max-w-6xl">
        <Header user={authUser} signOut={signOut} />
        {renderCurrentView()}
      </div>
      <TabNavigation 
        activeTab={state.currentView} 
        onTabChange={(tab) => {
          setCurrentView(tab as 'dashboard' | 'tasks' | 'add-task' | 'settings');
        }} 
      />
    </div>
  );
};

const AppContent: React.FC = () => {
  const [pendingGroupData, setPendingGroupData] = React.useState<{ familyId: string; familyName?: string } | undefined>();

  return (
    <Routes>
      <Route path="/invite" element={<InvitePage />} />
      <Route 
        path="/*" 
        element={
          <CustomAuthenticator
            onWeddingGroupCreate={async (familyId: string, familyName?: string) => {
              console.log('🎯 App.tsx: onWeddingGroupCreate called - FamilyID:', familyId, 'FamilyName:', familyName);
              // ウェディンググループ作成データを保存（認証後に処理される）
              setPendingGroupData({ familyId, familyName });
              console.log('✅ App.tsx: pendingGroupData set, waiting for authentication...');
            }}
            checkFamilyIdAvailability={async (familyId: string) => {
              // 一時的にFamily IDチェックを無効化（認証前はAPIが使用できないため）
              console.log('🔍 App.tsx: Family ID check skipped (pre-auth):', familyId);
              return true; // 常に利用可能として扱う
            }}
          >
            {({ signOut, user }) => {
              console.log('🔄 App.tsx: CustomAuthenticator render - User exists:', !!user);
              console.log('📝 App.tsx: User details:', user ? { 
                username: user.username, 
                signInDetails: user.signInDetails?.loginId 
              } : 'No user');
              
              return (
                <WeddingProvider user={user}>
                  <AuthenticatedAppWithGroupCreation 
                    pendingGroupData={pendingGroupData} 
                    user={user} 
                    signOut={signOut} 
                  />
                </WeddingProvider>
              );
            }}
          </CustomAuthenticator>
        } 
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App; 